import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface NeuInputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  label?: string;
}

export const NeuInput = forwardRef<HTMLInputElement, NeuInputProps>(
  ({ error, label, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s/g, '-');

    return (
      <div className="flex flex-col gap-1.5">
        {label ? (
          <label htmlFor={inputId} className="text-sm font-medium text-sime-text-secondary">
            {label}
          </label>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            'w-full rounded-xl px-4 py-3 min-h-[44px] bg-transparent',
            // Explicit border for dark-mode visibility (the neumorphic shadow alone is too subtle on AMOLED).
            'border border-sime-border-subtle',
            'shadow-neu-btn-active',
            'focus:ring-2 focus:ring-sime-accent focus:outline-none',
            'placeholder:text-sime-text-tertiary',
            'text-sime-text-primary',
            'transition-shadow duration-150',
            error && 'ring-2 ring-sime-danger',
            className,
          )}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        {error ? (
          <p id={`${inputId}-error`} className="text-sm text-sime-danger" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

NeuInput.displayName = 'NeuInput';
